<template>
  <div class="docs-page-viewer my-2" style="max-width: 95%;">
    <v-card variant="flat" rounded="lg" class="docs-card">
      <div
        class="docs-header d-flex align-center ga-2 px-4 py-2"
        @click="expanded = !expanded"
      >
        <v-icon :icon="expanded ? 'mdi-chevron-down' : 'mdi-chevron-right'" size="small" color="medium-emphasis" />
        <v-icon icon="mdi-book-open-page-variant-outline" size="small" color="info" />
        <span class="text-body-2 font-weight-medium flex-grow-1 text-truncate">{{ displayTitle }}</span>
        <v-btn
          v-if="url"
          icon
          size="x-small"
          variant="text"
          :href="url"
          target="_blank"
          :title="t('chat.openInDocsSite')"
          @click.stop
        >
          <v-icon size="14">mdi-open-in-new</v-icon>
        </v-btn>
      </div>

      <template v-if="expanded">
        <div v-if="cleanedContent" class="docs-content-scroll pa-4" style="border-top: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));">
          <MarkdownContent :content="cleanedContent" />
        </div>
        <div v-else class="pa-4 text-body-2 text-medium-emphasis">
          {{ t('chat.noPageContent') }}
        </div>
      </template>
    </v-card>
  </div>
</template>

<script setup>
import { useI18n } from '../../composables/useI18n.js'
const { t } = useI18n()
import { ref, computed } from 'vue'
import MarkdownContent from './MarkdownContent.vue'

const props = defineProps({
  page: { type: String, required: true },
  url: { type: String, default: '' },
  content: { type: String, default: '' },
})

const expanded = ref(true)

const displayTitle = computed(() => {
  const path = props.page.replace(/^\//, '').replace(/\.mdx$/, '')
  const segments = path.split('/')
  const last = segments[segments.length - 1] || path
  return last
    .split('-')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
})

const cleanedContent = computed(() => {
  if (!props.content) return ''

  let raw = props.content

  // MCP filesystem fallback wraps the markdown in JSON with a shell-style
  // "--- stdout ---" prefix; peel both layers off when present so the
  // renderer sees plain markdown.
  try {
    const parsed = JSON.parse(raw)
    if (parsed.content && Array.isArray(parsed.content)) {
      raw = parsed.content.filter(c => c.text).map(c => c.text).join('\n')
      const marker = '--- stdout ---\n'
      const idx = raw.indexOf(marker)
      if (idx >= 0) raw = raw.substring(idx + marker.length)
    }
  } catch {
    // Not JSON — raw markdown from the source, use as-is
  }

  return cleanMdx(raw)
})

// Strip MDX frontmatter and JSX-style component tags that a plain markdown
// renderer can't handle, keeping the prose and images intact.
function cleanMdx(raw) {
  let text = raw
  text = text.replace(/^---[\s\S]*?---\n*/, '')
  text = text.replace(/^import\s+.*$/gm, '')
  text = text.replace(/<(?!img\b)\w+[^>]*\/>/gi, '')
  text = text.replace(/<(\w+)[^>]*>([\s\S]*?)<\/\1>/g, '$2')
  return text.trim()
}
</script>

<style scoped>
.docs-card {
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(var(--v-theme-on-surface), 0.08) !important;
}
.docs-header {
  cursor: pointer;
}
.docs-header:hover {
  background: rgba(var(--v-theme-on-surface), 0.04);
}
.docs-content-scroll {
  max-height: 600px;
  overflow-y: auto;
  font-size: 0.85rem;
  line-height: 1.5;
}
.docs-content-scroll :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: 6px;
}
.docs-content-scroll::-webkit-scrollbar {
  width: 6px;
}
.docs-content-scroll::-webkit-scrollbar-thumb {
  background: rgba(var(--v-theme-on-surface), 0.15);
  border-radius: 3px;
}
</style>
